import { Component, Host, h, Prop, Event, Method, Element, Watch } from '@stencil/core';
import { beforeWrite, createPopper } from '@popperjs/core';
import { floatingElements } from '../../utils/floating-elements.utils';
const ESCAPE_KEY = 'Escape';
const PLACEMENTS = {
  'top-start': 'top-start',
  'top-end': 'top-end',
  'bottom-end': 'bottom-end',
  'bottom-start': 'bottom-start',
  'right': 'right-start',
  'left': 'left-start',
  'auto': 'auto',
};
/**
 * @slot  - Dropdown contents.
 */
export class Dropdown {
  constructor() {
    this.openedEventListeners = [];
    this.eventListeners = [];
    /**
     * Describes the preferred placement of the dropdown content.
     * The "auto" placements will choose the side with most space.
     * The placement may change to make de dropdown fit better.
     */
    this.placement = 'bottom-start';
    /**
     * Positioning strategy.
     * Use fixed when it is inside a fixed container.
     */
    this.display = 'absolute';
    /**
     * It indicates if the dropdown is open.
     *
     */
    this.opened = false;
    /**
     * If true, the dropdown will not close by pressing the ESC key.
     */
    this.ignoreEscape = false;
    /**
     * If true, the dropdown will not close with an outside click.
     */
    this.ignoreOutsideClick = false;
    /**
     * If true, the dropdown will be the same width as the container element.
     */
    this.sameContainerWidth = false;
    /**
     * Displaces the dropdown away from, or toward, the reference element in the direction of its placement.
     * A positive number displaces it further away, while a negative number lets it overlap the reference.
     */
    this.offsetDistance = 2;
  }
  async selectElementsWatcher() {
    const rootNode = this.el.getRootNode();
    this.toggleByElement = this.toggleBy ? rootNode.querySelector(this.toggleBy) : null;
    this.containerElement = this.container ? rootNode.querySelector(this.container) || this.el.parentElement : this.el.parentElement;
    this.handleContainerResize();
  }
  async openedWatcher() {
    if (this.opened === this.isOpenedInternal) {
      return;
    }
    if (this.opened) {
      return await this.open();
    }
    this.close();
  }
  /**
   * Open dropdown
   */
  async open() {
    if (this.isOpenedInternal)
      return;
    const popperConfig = this.getPopperConfig();
    floatingElements.open(this.el);
    this.popper = await createPopper(this.containerElement, this.el, popperConfig);
    this.isOpenedInternal = true;
    this.opened = true;
    this.syDropdownDidOpen.emit();
  }
  /**
   * Close dropdown
   */
  async close() {
    if (!this.isOpenedInternal)
      return;
    if (this.popper) {
      this.popper.destroy();
    }
    this.removeListeners(this.openedEventListeners);
    this.isOpenedInternal = false;
    this.opened = false;
    this.syDropdownDidClose.emit();
    floatingElements.close(this.el);
  }
  /**
   * Toggle dropdown
   */
  async toggle(event) {
    const isInsideClick = Boolean(((event === null || event === void 0 ? void 0 : event.composedPath()) || []).find(p => {
      if (p instanceof Node) {
        return this.el.contains(p);
      }
    }));
    if (!isInsideClick) {
      this.opened = !this.opened;
    }
  }
  async componentDidLoad() {
    await this.selectElementsWatcher();
    await this.addToggleByHandler();
  }
  componentDidRender() {
    if (this.opened) {
      this.addEscapeHandler();
      this.addOutsideClickHandler();
      if (this.popper)
        this.popper.update();
    }
  }
  disconnectedCallback() {
    if (this.el.isConnected)
      return;
    this.removeListeners(this.openedEventListeners);
    this.removeListeners(this.eventListeners);
    this.resizeObserver && this.resizeObserver.disconnect();
  }
  handleContainerResize() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const currentHeight = entry.target.clientHeight;
          if (!this.latestContainerHeight)
            this.latestContainerHeight = currentHeight;
          else if (this.latestContainerHeight != currentHeight && this.popper) {
            this.popper.update();
            this.latestContainerHeight = currentHeight;
          }
        }
      });
      this.resizeObserver.observe(this.containerElement);
    }
  }
  async addToggleByHandler() {
    if (this.toggleByElement) {
      const handler = this.toggle.bind(this);
      this.eventListeners.push({ element: this.toggleByElement, event: 'click', handler });
      this.toggleByElement.addEventListener('click', handler);
    }
  }
  escapeHandler(event) {
    var _a;
    if (this.ignoreEscape)
      return;
    if (event.key === ESCAPE_KEY) {
      this.close();
      const element = this.toggleByElement;
      (_a = element) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }
  addEscapeHandler() {
    if (this.ignoreEscape)
      return;
    const event = 'keyup';
    const handler = this.escapeHandler.bind(this);
    document.addEventListener(event, handler);
    this.openedEventListeners.push({ element: document, event, handler });
  }
  outsideClickHandler(event) {
    const isInsideClick = Boolean(((event === null || event === void 0 ? void 0 : event.composedPath()) || []).find(p => {
      if (p instanceof Node) {
        return this.el.contains(p);
      }
    }));
    if (!isInsideClick && this.opened) {
      this.close();
    }
  }
  addOutsideClickHandler() {
    if (this.ignoreOutsideClick)
      return;
    const event = 'click';
    const handler = this.outsideClickHandler.bind(this);
    document.addEventListener(event, handler);
    this.openedEventListeners.push({ element: document, event, handler });
  }
  removeListeners(listeners) {
    (listeners || []).forEach(listener => {
      listener.element.removeEventListener(listener.event, listener.handler);
    });
  }
  getPopperConfig() {
    const modifiers = [
      {
        name: 'offset',
        options: {
          offset: [0, this.offsetDistance],
        },
      },
    ];
    if (this.sameContainerWidth) {
      const sameWidth = {
        name: 'sameWidth',
        enabled: true,
        phase: beforeWrite,
        requires: ['computeStyles'],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
          state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
      };
      modifiers.push(sameWidth);
    }
    const defaultPopperConfig = {
      placement: PLACEMENTS[this.placement],
      strategy: '',
      modifiers,
    };
    return Object.assign({}, defaultPopperConfig);
  }
  render() {
    return (h(Host, null,
      h("slot", null)));
  }
  static get is() { return "sy-dropdown"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["scss/index.scss", "scss/_tokens.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["./scss/index.css", "./scss/_tokens.css"]
  }; }
  static get properties() { return {
    "toggleBy": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Element that toggle dropdown."
      },
      "attribute": "toggle-by",
      "reflect": false
    },
    "container": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Element that dropdown will be connected to.\nIf null the dropdown will be connected to the toggleBy element."
      },
      "attribute": "container",
      "reflect": false
    },
    "placement": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "Placement",
        "resolved": "\"auto\" | \"bottom-end\" | \"bottom-start\" | \"left\" | \"right\" | \"top-end\" | \"top-start\"",
        "references": {
          "Placement": {
            "location": "local"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Describes the preferred placement of the dropdown content.\nThe \"auto\" placements will choose the side with most space.\nThe placement may change to make de dropdown fit better."
      },
      "attribute": "placement",
      "reflect": true,
      "defaultValue": "'bottom-start'"
    },
    "display": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'absolute' | 'fixed'",
        "resolved": "\"absolute\" | \"fixed\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Positioning strategy.\nUse fixed when it is inside a fixed container."
      },
      "attribute": "display",
      "reflect": true,
      "defaultValue": "'absolute'"
    },
    "opened": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "It indicates if the dropdown is open."
      },
      "attribute": "opened",
      "reflect": true,
      "defaultValue": "false"
    },
    "ignoreEscape": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "If true, the dropdown will not close by pressing the ESC key."
      },
      "attribute": "ignore-escape",
      "reflect": true,
      "defaultValue": "false"
    },
    "ignoreOutsideClick": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "If true, the dropdown will not close with an outside click."
      },
      "attribute": "ignore-outside-click",
      "reflect": true,
      "defaultValue": "false"
    },
    "sameContainerWidth": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "If true, the dropdown will be the same width as the container element."
      },
      "attribute": "same-container-width",
      "reflect": false,
      "defaultValue": "false"
    },
    "offsetDistance": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Displaces the dropdown away from, or toward, the reference element in the direction of its placement.\nA positive number displaces it further away, while a negative number lets it overlap the reference."
      },
      "attribute": "offset-distance",
      "reflect": true,
      "defaultValue": "2"
    },
    "popperConfig": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "{ [k: string]: any }",
        "resolved": "{ [k: string]: any; }",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Overlay advanced configs. <https://popper.js.org/>"
      }
    }
  }; }
  static get events() { return [{
      "method": "syDropdownDidOpen",
      "name": "syDropdownDidOpen",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fired when dropdown is opened."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "syDropdownDidClose",
      "name": "syDropdownDidClose",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fired when dropdown is closed."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }]; }
  static get methods() { return {
    "open": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLElement": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Open dropdown",
        "tags": []
      }
    },
    "close": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Close dropdown",
        "tags": []
      }
    },
    "toggle": {
      "complexType": {
        "signature": "(event?: any) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Toggle dropdown",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "el"; }
  static get watchers() { return [{
      "propName": "toggleBy",
      "methodName": "selectElementsWatcher"
    }, {
      "propName": "container",
      "methodName": "selectElementsWatcher"
    }, {
      "propName": "opened",
      "methodName": "openedWatcher"
    }]; }
}
